import React, {useMemo, useState} from 'react';

import {css} from 'emotion';
import moment from 'moment-tz';
import {components, type OptionProps} from 'react-select';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {FormControlGroup} from 'app/general/forms';
import {SidebarSectionHeader} from 'app/general/structures/DocSidebarPagePanels';
import StateButton from 'app/general/widgets/StateButton';
import {SweetDatePicker} from 'app/general/widgets/SweetDatePicker';
import ThreeDotsDropdownMenu from 'app/general/widgets/ThreeDotsDropdownMenu';
import {RolesSelectSingle} from 'app/roles/components/RoleSelect';
import {getRoleLabel} from 'app/roles/utils';
import {gt, ngt} from 'app/utils/i18n';

import type {MenuItemsType} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {BasicRoleSerializedType} from 'sweet-types';

type Props = {
  enabled: boolean;
  reviewer: BasicRoleSerializedType | null;
  nextReviewAt: string;
  reviewPeriodMonths: number;
  isEditing: boolean;
  setEditing: (isEditing: boolean) => unknown;
  teamIds: Array<number>;
  toggleReview: (
    nextReviewDate: string | null,
    reviewPeriodMonths: number | null,
    reviewer: string | null
  ) => Promise<any>;
};

export function ReviewMeta(props: Props): JSX.Element {
  // props
  const {enabled, toggleReview, reviewer, isEditing, setEditing, teamIds} =
    props;

  // states
  const [nextReviewer, setNextReviewer] =
    useState<BasicRoleSerializedType | null>(reviewer);
  const [nextReviewAt, setNextReviewAt] = useState<string>(props.nextReviewAt);
  const [reviewPeriodMonths, setReviewPeriodMonths] = useState<number>(
    props.reviewPeriodMonths
  );

  const DATE = useMemo(() => moment(nextReviewAt).format('ll'), [nextReviewAt]);

  const setReview = () => {
    const url: string | null = nextReviewer?.url ?? null;
    return toggleReview(nextReviewAt, reviewPeriodMonths, url).then(() =>
      setEditing(false)
    );
  };

  const getMenuActions = (): MenuItemsType => {
    return [
      {
        text: gt('Edit review date/interval'),
        icon: 'fas fa-pencil',
        key: 'edit',
        condition: () => true,
        callback: () => {
          setEditing(true);
          return Promise.resolve(true);
        },
      },
      {
        text: gt('Disable reviews'),
        icon: 'fas fa-times',
        key: 'disable',
        condition: () => true,
        callback: () => toggleReview(null, null, null),
      },
    ];
  };

  const renderToggleButton = (title: string, direction: 'up' | 'down') => {
    return (
      <button
        className="btn btn-sm btn-link p-0"
        onClick={() => setEditing(!isEditing)}
      >
        {title} <i className={`fas fa-chevron-${direction}`} />
      </button>
    );
  };

  return (
    <SidebarSectionHeader
      title={enabled ? gt('Reviews Enabled') : gt('Reviews Disabled')}
      actionButton={
        isEditing ? (
          renderToggleButton(gt('Cancel'), 'up')
        ) : enabled ? (
          <ThreeDotsDropdownMenu
            actionsGetter={getMenuActions}
            object={null}
            className="btn-sm mr-n2"
          />
        ) : (
          renderToggleButton(gt('Enable'), 'down')
        )
      }
    >
      {isEditing ? (
        <>
          <FormControlGroup field="reviewer">
            <label>{gt('Reviewer')}:</label>
            <RolesSelectSingle
              disabled={false}
              lazyFetch={true}
              selected={nextReviewer}
              onChange={(value: BasicRoleSerializedType) => {
                setNextReviewer(value);
                return Promise.resolve(true);
              }}
              placeholder={gt('Select role to review this document.')}
              selectProps={{
                autoFocus: true,
                isOptionDisabled: (role: BasicRoleSerializedType) =>
                  !teamIds.includes(role.team.id),
              }}
              components={{Option: ExplainUnselectableRole}}
            />
          </FormControlGroup>
          <FormControlGroup field="next_review_at">
            <label>{gt('Next review date')}:</label>
            <SweetDatePicker
              className="form-control form-control-sm"
              value={[nextReviewAt]}
              onClose={() => {
                if (nextReviewAt === '') {
                  setNextReviewAt(nextReviewAt);
                }
              }}
              onChange={([nextReviewAt]) =>
                setNextReviewAt(
                  nextReviewAt != null ? nextReviewAt.toISOString() : ''
                )
              }
            />
          </FormControlGroup>
          <FormControlGroup field="review_period_months">
            <label>{gt('Review interval in months')}:</label>
            <input
              type="number"
              className="form-control form-control-sm"
              value={reviewPeriodMonths}
              onChange={(e) => setReviewPeriodMonths(parseInt(e.target.value))}
            />
          </FormControlGroup>
          <StateButton
            onClick={setReview}
            busyText={enabled ? gt('Updating...') : gt('Enabling...')}
            className="btn btn-block btn-primary btn-sm"
          >
            {enabled ? gt('Update reviews') : gt('Enable reviews')}
          </StateButton>
        </>
      ) : enabled ? (
        <>
          <div>{gt('Reviewer: %s', <ReviewerRole reviewer={reviewer} />)}</div>
          <div>{gt('Next review date: %s', <b>{DATE}</b>)}</div>
          <div>
            {ngt(
              'Review every %s month',
              'Review every %s months',
              reviewPeriodMonths
            )}
          </div>
        </>
      ) : null}
    </SidebarSectionHeader>
  );
}

type ReviewerProps = {
  reviewer: BasicRoleSerializedType | null;
};

const ReviewerRole = ({reviewer}: ReviewerProps): JSX.Element => {
  if (reviewer != null) {
    return (
      <FullHTMLLink html_url={reviewer.html_url}>
        {getRoleLabel(reviewer)}
      </FullHTMLLink>
    );
  } else {
    return <b>{gt('Unassigned')}</b>;
  }
};

const ExplainUnselectableRole = (props: OptionProps) => {
  return props.isDisabled ? (
    <LazyTooltip
      tooltip={gt(
        'First add this document to the %s team',
        props.data.team.name
      )}
      placement="bottom"
    >
      <div
        ref={props.innerRef}
        {...props.innerProps}
        className={props.cx(
          css(props.getStyles('option', props), {
            option: true,
            'option--is-disabled': true,
            'option--is-focused': false,
            'option--is-selected': false,
          })
        )}
      >
        <span className="text-muted font-italic">
          {getRoleLabel(props.data)}
        </span>
      </div>
    </LazyTooltip>
  ) : (
    <components.Option {...props} />
  );
};
